/**
 * Accordions – component-based controller for sections/accordions.liquid.
 *
 * Wraps the original accordion module in an <accordion-section> custom
 * element so each section instance is initialised on connect and torn
 * down on disconnect (theme-editor friendly).
 *
 * Behavior preserved from the original:
 *   - Click a [data-controls] button to toggle its associated panel.
 *   - Animate height: 0 ↔ scrollHeight, then set height: auto on expand.
 *   - aria-expanded / aria-hidden / aria-controls / aria-labelledby kept
 *     in sync for accessibility.
 *   - Honors the initial state set in markup: a panel rendered with
 *     aria-hidden="false" stays open after bind.
 *   - Focuses the panel content after expand.
 */

const SELECTOR_ACCORDION = '.js-accordion';
const SELECTOR_BUTTON = '[data-controls]';
const RENDER_DELAY = 25;
const ANIMATION_DELAY = 425;

class AccordionSection extends HTMLElement {
  constructor() {
    super();
    this._onButtonClick = this._onButtonClick.bind(this);
    this._buttons = [];
  }

  connectedCallback() {
    this._init();
  }

  disconnectedCallback() {
    this._destroy();
  }

  /* --------------------------------------------------------------------- */
  /* Init / destroy                                                        */
  /* --------------------------------------------------------------------- */

  _init() {
    this.querySelectorAll(`${SELECTOR_ACCORDION} ${SELECTOR_BUTTON}`).forEach((buttonEl) => {
      this._bindAccordion(buttonEl);
    });
  }

  _destroy() {
    this._buttons.forEach((buttonEl) => this._unbindAccordion(buttonEl));
    this._buttons = [];
  }

  /* --------------------------------------------------------------------- */
  /* Per-button bind / unbind                                              */
  /* --------------------------------------------------------------------- */

  _bindAccordion(buttonEl, { initExpanded = false } = {}) {
    const controlsId = buttonEl.getAttribute('data-controls');
    if (!controlsId) return;

    const panelEl = document.getElementById(controlsId);
    if (!panelEl) return;

    buttonEl._panelEl = panelEl;
    buttonEl.addEventListener('click', this._onButtonClick);
    this._buttons.push(buttonEl);

    const isExpanded = panelEl.getAttribute('aria-hidden') === 'false' || initExpanded;

    buttonEl.setAttribute('aria-controls', panelEl.id);
    buttonEl.setAttribute('aria-expanded', isExpanded.toString());

    panelEl.setAttribute('style', isExpanded ? 'height: auto' : 'height: 0px');
    panelEl.setAttribute('aria-hidden', (!isExpanded).toString());
    if (buttonEl.id) panelEl.setAttribute('aria-labelledby', buttonEl.id);
  }

  _unbindAccordion(buttonEl) {
    buttonEl.removeEventListener('click', this._onButtonClick);

    if (buttonEl._renderTimeout) clearTimeout(buttonEl._renderTimeout);
    if (buttonEl._animationTimeout) clearTimeout(buttonEl._animationTimeout);

    const panelEl = buttonEl._panelEl;
    buttonEl.removeAttribute('aria-controls');
    buttonEl.removeAttribute('aria-expanded');

    if (panelEl) {
      panelEl.removeAttribute('style');
      panelEl.removeAttribute('aria-hidden');
      panelEl.removeAttribute('aria-labelledby');
    }
    buttonEl._panelEl = null;
  }

  /* --------------------------------------------------------------------- */
  /* Toggle                                                                */
  /* --------------------------------------------------------------------- */

  _onButtonClick(ev) {
    const buttonEl = ev.currentTarget;
    const panelEl = buttonEl._panelEl;
    if (!panelEl || !panelEl.firstElementChild) return;

    const willExpand = buttonEl.getAttribute('aria-expanded') === 'false';
    const height = panelEl.firstElementChild.scrollHeight;

    clearTimeout(buttonEl._renderTimeout);
    clearTimeout(buttonEl._animationTimeout);

    buttonEl.setAttribute('aria-expanded', willExpand);
    panelEl.setAttribute('style', willExpand ? 'height: 0px' : `height: ${height}px`);

    buttonEl._renderTimeout = setTimeout(() => {
      panelEl.setAttribute('style', willExpand ? `height: ${height}px` : 'height: 0px');
      panelEl.setAttribute('aria-hidden', (!willExpand).toString());
      if (willExpand) this._focusContent(panelEl.firstElementChild);
    }, RENDER_DELAY);

    buttonEl._animationTimeout = setTimeout(() => {
      panelEl.setAttribute('style', willExpand ? 'height: auto' : 'height: 0px');
    }, ANIMATION_DELAY);
  }

  _focusContent(contentEl) {
    if (contentEl.tagName === 'DIV') contentEl.setAttribute('tabindex', '-1');
    contentEl.focus();
    if (contentEl.tagName === 'DIV') contentEl.removeAttribute('tabindex');
  }
}

if (!customElements.get('accordion-section')) {
  customElements.define('accordion-section', AccordionSection);
}
